/**
 * IraInstitutionRelationshipsPage — Reference Data · IRA Institution Relationships.
 *
 * Q3 carve-out catalog for broker-dealers, custodians, and receiving
 * institutions. Per Bill's 2026-04-25 sign-off Q3: the Raymond James / Verna
 * early-withdrawal penalty exemption is NOT hardcoded — admin manages it as a
 * row here (PenaltyExempt=true). Any institution whose distributions need an
 * extra manager signoff is flagged with RequiresAdditionalApproval=true.
 *
 * Contract (verified against the backend 2026-06-16 — entity/DTO/service/read
 * route all agree; no drift):
 *   - Read:   GET /v3/config/ira-institution-relationships
 *               → ApiResult<IraInstitutionRelationship[]> (data IS the array).
 *               V3ConfigFunction.cs:696-725 — Select projects camelCase fields
 *               { code, displayName, relationshipType, penaltyExempt,
 *                 requiresAdditionalApproval, notes, isActive, … } and FILTERS
 *               `WHERE r.IsActive` → inactive rows are not returned.
 *   - Write:  POST /v3/admin/ira-institution-relationships
 *               V3AdminFunction.cs:800-883 — persists EVERY field, no silent
 *               drop (PenaltyExempt + RequiresAdditionalApproval both written).
 *   - Delete: DELETE /v3/admin/ira-institution-relationships/{code}
 *               V3AdminFunction.cs:885-910 — HARD delete (_db.Remove). The
 *               entity has IsActive, but the config read hides inactive rows,
 *               so a soft-delete would vanish with no path to reactivate. We
 *               therefore use the hard-delete route for removal and still
 *               expose IsActive as an editable field in the modal.
 *
 * Typed surface (api-client):
 *   - client.retail.config.iraInstitutionRelationships()
 *   - client.retail.admin.iraInstitutionRelationships.upsert(SENTINEL, body)
 *   - client.retail.admin.iraInstitutionRelationships.remove(SENTINEL, code)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type IraInstitutionRelationshipUpsertBody,
} from '@retail-ops/api-client';
import type { IraInstitutionRelationship } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface RelRow {
  code: string;
  displayName: string;
  relationshipType: string;
  penaltyExempt: boolean;
  requiresAdditionalApproval: boolean;
  notes: string | null;
  isActive: boolean;
}

interface EditDraft extends RelRow {
  isNew: boolean;
}

const RELATIONSHIP_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'BROKER_DEALER', label: 'Broker-Dealer' },
  { value: 'CUSTODIAN', label: 'Custodian' },
  { value: 'RECEIVING_INSTITUTION', label: 'Receiving institution' },
];

function relationshipTypeLabel(code: string): string {
  return RELATIONSHIP_TYPES.find((t) => t.value === code)?.label ?? code;
}

function emptyDraft(): EditDraft {
  return {
    code: '',
    displayName: '',
    relationshipType: 'BROKER_DEALER',
    penaltyExempt: false,
    requiresAdditionalApproval: false,
    notes: '',
    isActive: true,
    isNew: true,
  };
}

function rowToDraft(row: RelRow): EditDraft {
  return { ...row, notes: row.notes ?? '', isNew: false };
}

function draftToUpsertBody(d: EditDraft): IraInstitutionRelationshipUpsertBody {
  const notes = d.notes?.trim();
  return {
    code: d.code.trim(),
    displayName: d.displayName.trim(),
    relationshipType: d.relationshipType,
    penaltyExempt: d.penaltyExempt,
    requiresAdditionalApproval: d.requiresAdditionalApproval,
    notes: notes ? notes : null,
    isActive: d.isActive,
  };
}

export function IraInstitutionRelationshipsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<RelRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RelRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.iraInstitutionRelationships();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: RelRow[] = resp.data.map((r: IraInstitutionRelationship) => ({
            code: r.code,
            displayName: r.displayName,
            relationshipType: r.relationshipType,
            penaltyExempt: r.penaltyExempt,
            requiresAdditionalApproval: r.requiresAdditionalApproval,
            notes: r.notes ?? null,
            isActive: r.isActive,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load IRA institution relationships.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    const n = filter.trim().toLowerCase();
    const sorted = (rows ?? []).slice().sort((a, b) => a.displayName.localeCompare(b.displayName));
    if (!n) return sorted;
    return sorted.filter(
      (r) =>
        r.code.toLowerCase().includes(n) ||
        r.displayName.toLowerCase().includes(n) ||
        r.relationshipType.toLowerCase().includes(n),
    );
  }, [rows, filter]);

  const upsert = useCallback(
    async (d: EditDraft): Promise<boolean> => {
      const resp = await client.retail.admin.iraInstitutionRelationships.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(d),
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const code = draft.code.trim();
    if (!code || !/^[A-Z0-9_]{1,32}$/.test(code)) {
      setSaveError('Code must be 1–32 chars (uppercase letters, digits, underscores).');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Institution name is required.');
      return;
    }
    if (!RELATIONSHIP_TYPES.some((t) => t.value === draft.relationshipType)) {
      setSaveError('Pick a relationship type.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created institution relationship "${draft.displayName}".`
          : `Updated "${draft.displayName}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.iraInstitutionRelationships.remove(
        ADMIN_AUTH_TOKEN_SENTINEL,
        deleteTarget.code,
      );
      if (!resp.success) throw new Error(resp.error ?? 'delete returned success=false');
      setToast({ tone: 'warn', text: `Removed "${deleteTarget.displayName}" from the catalog.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<RelRow>> = useMemo(
    () => [
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.code}</code> },
      {
        header: 'Institution',
        cell: (r) => (
          <>
            <span className="cell-strong">{r.displayName}</span>
            {r.notes && <div className="cell-desc">{r.notes}</div>}
          </>
        ),
      },
      { header: 'Relationship', cell: (r) => relationshipTypeLabel(r.relationshipType) },
      {
        header: 'Penalty-exempt',
        cell: (r) => (r.penaltyExempt ? <Pill tone="info">exempt</Pill> : <Pill tone="muted">no</Pill>),
      },
      {
        header: 'Extra approval',
        cell: (r) =>
          r.requiresAdditionalApproval ? <Pill tone="warn">required</Pill> : <Pill tone="muted">no</Pill>,
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            <Button onClick={() => { setDeleteTarget(r); setSaveError(null); }}>Delete</Button>
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load IRA institution relationships: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading IRA institution relationships…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Penalty-exempt rows skip the early-withdrawal penalty calc on distributions to/from this
        institution. The procedure-doc citation for Raymond James / Verna lives in the seed file —
        new exemptions should reference equivalent guidance in the Notes field.
      </Banner>

      <Card
        title={`IRA Institution Relationships — ${filtered.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="code, name, or relationship type…"
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Institution
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          Carve-out catalog for broker-dealers, custodians, and receiving institutions. Mark
          penalty-exempt partners (e.g. Raymond James / Verna) and any institution whose
          distributions need an extra manager signoff.
        </p>
        <DataTable<RelRow>
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.code}
          emptyState={<p className="empty">No institution relationships match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add an institution relationship' : `Edit ${draft?.displayName || draft?.code || ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Code"
                value={draft.code}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({
                    ...draft,
                    code: v.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 32),
                  })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. RAYMOND_JAMES_VERNA"
                hint={draft.isNew ? '1–32 chars. Uppercase, digits, underscores. Permanent.' : 'Permanent — cannot change after creation.'}
              />
              <TextField
                label="Institution name"
                value={draft.displayName}
                onChange={(v) => setDraft({ ...draft, displayName: v.slice(0, 128) })}
                placeholder="e.g. Raymond James / Verna"
                hint="Plain-language name shown to the FSR in the IRA wizard."
              />
            </div>

            <SelectField
              label="Relationship type"
              value={draft.relationshipType}
              onChange={(v) => setDraft({ ...draft, relationshipType: v })}
              options={RELATIONSHIP_TYPES}
              hint="Broker-dealer, custodian, or receiving institution."
            />

            <BoolField
              label="Penalty-exempt"
              value={draft.penaltyExempt}
              onChange={(v) => setDraft({ ...draft, penaltyExempt: v })}
              hint="When checked, distributions to/from this institution skip the early-withdrawal penalty calc."
            />

            <BoolField
              label="Requires additional approval"
              value={draft.requiresAdditionalApproval}
              onChange={(v) => setDraft({ ...draft, requiresAdditionalApproval: v })}
              hint="When checked, posting against this institution waits for officer + manager signoff."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive rows are hidden from the IRA wizard (and from this catalog list after save)."
            />

            <TextArea
              label="Notes"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              rows={3}
              placeholder="e.g. PCB procedure-doc §2.2.6 — penalty-exempt carve-out."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Remove this institution?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Remove'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll remove <strong>{deleteTarget.displayName}</strong> (
              <code className="cell-mono">{deleteTarget.code}</code>) from the catalog. Distributions
              involving this institution fall back to default penalty + approval rules. This is a
              permanent delete — to keep the row for audit, edit it and uncheck <em>Active</em> instead.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
